import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../utils/constants';
import { buildHeaders } from '../services/apiService';
import AdminEditLeadModal from './admin/AdminEditLeadModal';

const UserSubmissionsModal = ({ isVisible, userName, userEmail, inspections, onClose, getSubmittedByName, adminMode = false }) => {
  const [userInspections, setUserInspections] = useState([]); // User's inspections with images
  const [loading, setLoading] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // 🔧 NEW: Force refresh after save
  const [groupedView, setGroupedView] = useState(true); // 🆕 Toggle for duplicate grouping view
  const [lightboxImage, setLightboxImage] = useState(null); // 🔧 Lightbox for fullscreen image view
  
  // Fetch user's inspections when modal opens or refresh triggered
  useEffect(() => {
    const lookupKey = userEmail || userName;
    if (isVisible && lookupKey) {
      const fetchUserData = async () => {
        setLoading(true);
        try {
          console.log(`📥 Fetching inspections for user: ${userName} (lookup: ${lookupKey})`);
          const endpoint = adminMode
            ? `/api/admin/inspections/user/${encodeURIComponent(lookupKey)}`
            : `/api/inspections/user/${encodeURIComponent(lookupKey)}`;
          const response = await fetch(`${BACKEND_URL}${endpoint}?limit=20&page=${page}`, { headers: buildHeaders() });
          
          if (response.ok) {
            const data = await response.json();
            const items = Array.isArray(data) ? data : (data.inspections || []);
            console.log(`✅ Loaded ${items.length} inspections for page ${page}`);
            setUserInspections(items);
            if (!Array.isArray(data)) {
              setTotalPages(data.total_pages || 1);
              setTotalCount(data.total || items.length);
            } else {
              setTotalPages(1);
              setTotalCount(items.length);
            }
          } else {
            console.error(`❌ Failed to fetch user inspections: ${response.status}`);
          }
        } catch (error) {
          console.error('Failed to load user inspections:', error);
        } finally {
          setLoading(false);
        }
      };
      
      fetchUserData();
    }
  }, [isVisible, userName, userEmail, page, refreshTrigger]);

  // Reset state when opening for a different user or when modal opens/closes
  useEffect(() => {
    if (isVisible) {
      setPage(1);
      setEditingInspection(null); // CRITICAL: Reset editing state when opening
    }
  }, [isVisible, userName]);
  
  // Also reset editing state when modal closes
  useEffect(() => {
    if (!isVisible) {
      setEditingInspection(null);
    }
  }, [isVisible]);
  
  if (!isVisible || !userName) return null;

  // 🆕 Helper: Extract GPS coordinates from inspection
  const extractGPS = (inspection) => {
    try {
      // Try gps_data field first
      let gps = inspection.gps_data;
      if (typeof gps === 'string') {
        gps = JSON.parse(gps);
      }
      if (gps && gps.latitude && gps.longitude) {
        return { lat: parseFloat(gps.latitude), lng: parseFloat(gps.longitude) };
      }
      
      // Try location.gps field
      if (inspection.location && inspection.location.gps) {
        const g = inspection.location.gps;
        if (g.lat && g.lng) {
          return { lat: parseFloat(g.lat), lng: parseFloat(g.lng) };
        }
      }
    } catch (e) {
      console.warn('GPS extraction failed:', e);
    }
    return null;
  };

  // 🆕 Group submissions by GPS location (same location = duplicates)
  const groupSubmissionsByLocation = (submissions) => {
    if (!groupedView) {
      // Ungrouped view - show all submissions flat
      return submissions.map(sub => ({
        canonical: sub,
        duplicates: [],
        locationKey: null
      }));
    }

    const locationGroups = {}; // "lat,lng" -> [inspections]
    const noGPS = []; // Submissions without GPS

    // Sort by created_at first (oldest first = canonical)
    const sorted = [...submissions].sort((a, b) => {
      const aTime = new Date(a.created_at || a.timestamp);
      const bTime = new Date(b.created_at || b.timestamp);
      return aTime - bTime;
    });

    sorted.forEach(sub => {
      const gps = extractGPS(sub);
      if (!gps) {
        noGPS.push(sub);
        return;
      }

      // Round to 6 decimals (~0.1m precision)
      const key = `${gps.lat.toFixed(6)},${gps.lng.toFixed(6)}`;
      
      if (!locationGroups[key]) {
        locationGroups[key] = [];
      }
      locationGroups[key].push(sub);
    });

    // Build groups: first submission = canonical, rest = duplicates
    const groups = [];
    
    Object.entries(locationGroups).forEach(([key, subs]) => {
      if (subs.length > 0) {
        groups.push({
          canonical: subs[0],
          duplicates: subs.slice(1),
          locationKey: key,
          locationName: subs[0].business_name || subs[0].location || 'Location'
        });
      }
    });

    // Add no-GPS submissions as individual groups
    noGPS.forEach(sub => {
      groups.push({
        canonical: sub,
        duplicates: [],
        locationKey: null
      });
    });

    // Sort groups by canonical created_at (most recent first)
    groups.sort((a, b) => {
      const aTime = new Date(a.canonical.created_at || a.canonical.timestamp);
      const bTime = new Date(b.canonical.created_at || b.canonical.timestamp);
      return bTime - aTime;
    });

    return groups;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    return new Date(timestamp).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Chicago'
    });
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = (status || 'pending').toLowerCase();
    
    if (normalizedStatus === 'approved') {
      return { text: 'approved', className: 'bg-green-100 text-green-800 border-green-200' };
    } else if (normalizedStatus === 'rejected') {
      return { text: 'rejected', className: 'bg-red-100 text-red-800 border-red-200' };
    } else {
      return { text: 'pending', className: 'bg-yellow-100 text-yellow-800 border-yellow-200' };
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      {/* 🔧 Lightbox Modal for Full-Size Image Viewing */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/95 z-[10000] flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          {/* Close Button - Top Right */}
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 z-10 w-12 h-12 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center text-white text-3xl font-bold transition-all backdrop-blur-sm border border-white/30 shadow-lg"
            aria-label="Close image"
          >
            ×
          </button>
          
          {/* Image - Portrait orientation optimized */}
          <img
            src={lightboxImage}
            alt="Full size inspection"
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Hint text */}
          <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            Click anywhere or × to close
          </p>
        </div>
      )}
      
      {/* Using AdminEditLeadModal for more reliable editing */}
      <AdminEditLeadModal
        isOpen={!!editingInspection}
        inspection={editingInspection}
        backendUrl={BACKEND_URL}
        onClose={() => setEditingInspection(null)}
        onSaved={(updated) => {
          // 🔧 FIX: Refetch the entire list from server instead of trying to update local state
          // This prevents the "duplicate submission" bug where all items become the same
          setEditingInspection(null);
          setRefreshTrigger(prev => prev + 1); // Trigger useEffect to refetch
        }}
      />
      
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl animate-scaleIn" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-[var(--pbfs-navy)] to-[var(--pbfs-blue)] text-white p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[var(--pbfs-green)] rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-lg">
                {userName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold">{userName}'s Submissions</h2>
              <p className="text-white/70 text-sm">{totalCount || userInspections.length} total submissions</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/70 hover:text-white text-2xl leading-none transition-colors w-8 h-8 flex items-center justify-center hover:bg-white/10 rounded-full"
          >
            ×
          </button>
        </div>

        {/* Content - List Style with Duplicate Grouping */}
        <div className="p-4 overflow-y-auto max-h-[calc(85vh-180px)] bg-gray-50">
          {/* Toggle View Button */}
          <div className="mb-3 flex items-center justify-between bg-white rounded-lg p-2 border border-gray-200">
            <span className="text-xs text-gray-600 font-medium">
              {groupedView ? '🔗 Grouped by Location' : '📋 All Submissions'}
            </span>
            <button
              onClick={() => setGroupedView(!groupedView)}
              className="text-xs bg-[var(--pbfs-blue)]/10 hover:bg-[var(--pbfs-blue)]/20 text-[var(--pbfs-blue)] px-3 py-1 rounded font-semibold transition-colors"
            >
              {groupedView ? 'Show All' : 'Group Duplicates'}
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin text-4xl mb-2">⚙️</div>
              <p className="text-gray-600">Loading submissions...</p>
            </div>
          ) : userInspections.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No submissions found</p>
            </div>
          ) : (
            <div className="space-y-4">
              {groupSubmissionsByLocation(userInspections).map((group, groupIdx) => {
                const hasDuplicates = group.duplicates.length > 0;
                
                return (
                  <div key={`group-${groupIdx}`} className="space-y-2">
                    {/* Canonical Submission */}
                    {(() => {
                      const inspection = group.canonical;
                const imageUrl = inspection.image_url ? `${BACKEND_URL}${inspection.image_url}` : null;
                const statusBadge = getStatusBadge(inspection.status);
                
                return (
                  <div
                          className={`bg-white rounded-lg p-3 hover:bg-gray-50 transition-all flex items-center gap-3 border-2 ${
                            hasDuplicates ? 'border-[var(--pbfs-green)]' : 'border-gray-200'
                          } hover:shadow-md group`}
                  >
                          {/* Thumbnail - Click opens lightbox */}
                    {imageUrl && (
                      <img
                        src={imageUrl}
                        alt="Tag"
                        className="w-16 h-16 rounded object-cover border-2 border-[var(--pbfs-green)] flex-shrink-0 cursor-pointer hover:scale-105 transition-transform"
                        onClick={(e) => {
                          e.stopPropagation();
                          setLightboxImage(imageUrl);
                        }}
                      />
                    )}
                    
                    {/* Details */}
                    <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {hasDuplicates && (
                                <span className="text-xs bg-[var(--pbfs-green)] text-white px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                                  ✓ CANONICAL
                                </span>
                              )}
                      <div className="font-semibold text-sm text-[var(--pbfs-navy)] truncate">
                        {inspection.business_name || inspection.location || 'Submission'}
                              </div>
                      </div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        {formatDate(inspection.timestamp || inspection.created_at)}
                      </div>
                      <div className="mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded border font-semibold ${statusBadge.className}`}>
                          {statusBadge.text}
                        </span>
                      </div>
                    </div>
                    
                          {/* Edit Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingInspection(inspection);
                      }}
                      className="bg-[var(--pbfs-blue)] hover:bg-[var(--pbfs-navy)] text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-1 flex-shrink-0 opacity-80 group-hover:opacity-100"
                    >
                      ✏️ Edit
                    </button>
                        </div>
                      );
                    })()}

                    {/* Duplicate Submissions (Indented) */}
                    {hasDuplicates && (
                      <div className="pl-6 space-y-2 border-l-2 border-orange-300 ml-2">
                        {group.duplicates.map((inspection, dupIdx) => {
                          const imageUrl = inspection.image_url ? `${BACKEND_URL}${inspection.image_url}` : null;
                          const statusBadge = getStatusBadge(inspection.status);
                          
                          return (
                            <div
                              key={`dup-${dupIdx}`}
                              className="bg-orange-50/50 rounded-lg p-3 hover:bg-orange-50 transition-all flex items-center gap-3 border border-orange-200 group"
                            >
                              {/* Duplicate Icon */}
                              <div className="flex-shrink-0 w-16 h-16 bg-orange-100 rounded border border-orange-300 flex items-center justify-center text-2xl">
                                🔄
                              </div>
                              
                              {/* Details */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full font-bold">
                                    DUPLICATE
                                  </span>
                                  <div className="font-medium text-sm text-gray-700 truncate">
                                    {inspection.business_name || inspection.location || 'Submission'}
                                  </div>
                                </div>
                                <div className="text-xs text-gray-600 mt-0.5">
                                  {formatDate(inspection.timestamp || inspection.created_at)}
                                </div>
                                <div className="mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded border font-semibold ${statusBadge.className}`}>
                                    {statusBadge.text}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Edit Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingInspection(inspection);
                                }}
                                className="bg-gray-400 hover:bg-gray-600 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-1 flex-shrink-0 opacity-70 group-hover:opacity-100"
                              >
                                ✏️ Edit
                              </button>
                            </div>
                          );
                        })}

                        {/* Review Duplicate Group Button */}
                        <div className="pt-2">
                          <button
                            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                            onClick={() => {
                              alert(`🚧 Feature Coming: Opens map at ${group.locationName} to review ${group.duplicates.length + 1} submissions at this location`);
                              // TODO: Open AnalyticsMap with pre-selected location
                            }}
                          >
                            <span>🗺️</span>
                            <span>Review {group.duplicates.length + 1} Submissions on Map</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer - Pagination */}
        {totalPages > 1 && (
          <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between">
            <p className="text-gray-600 text-sm">
              Page {page} of {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 border border-gray-300 rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed font-medium transition-colors"
              >
                ← Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 border border-gray-300 rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed font-medium transition-colors"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSubmissionsModal;
